(() => {
  if (customElements.get('dawn-purchase-options')) return;
  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.plans = JSON.parse(this.querySelector('[data-plan-data]').textContent);
      this.input = this.querySelector('[data-owned-plan]');
      this.form = document.getElementById(this.dataset.form);
      this.status = this.querySelector('[data-plan-status]');
      this.boxes = [...this.querySelectorAll('.subscription__box')];
      this.mode = this.dataset.required === 'true' ? 'subscription' : this.dataset.preselected;
      this.planId = this.plans[0]?.id || '';
      this.boxes.forEach(box => {
        box.tabIndex = 0;
        box.setAttribute('role', this.dataset.isCheckbox === 'true' ? 'checkbox' : 'radio');
      });
      this.querySelector('.subscription__wrapper').setAttribute('role', this.dataset.isCheckbox === 'true' ? 'group' : 'radiogroup');
      const activate = box => {
        this.mode = box.dataset.type;
        if (this.dataset.isCheckbox === 'true' && this.dataset.required !== 'true') {
          this.mode = this.input.disabled ? 'subscription' : 'one_time';
        }
        this.sync();
      };
      this.addEventListener('click', e => {
        if (e.target.closest('select,input,label,a,.subscription__frequency')) return;
        const box = e.target.closest('.subscription__box');
        if (box) activate(box);
      }, { signal: this.abort.signal });
      this.addEventListener('keydown', e => {
        if (!this.boxes.includes(e.target)) return;
        if ([' ', 'Enter'].includes(e.key)) { e.preventDefault(); activate(e.target); }
        if (this.boxes.length > 1 && ['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) {
          e.preventDefault();
          const next = this.boxes[(this.boxes.indexOf(e.target) + 1) % this.boxes.length];
          next.focus(); activate(next);
        }
      }, { signal: this.abort.signal });
      this.addEventListener('change', e => {
        if (!e.target.matches('select,input[data-selling-plan-id]')) return;
        this.planId = String(e.target.value);
        this.mode = 'subscription';
        this.sync();
      }, { signal: this.abort.signal });
      this.form?.addEventListener('submit', e => {
        this.sync();
        if (this.pending || this.conflict || !this.form || (this.mode === 'subscription' && !this.plans.some(p => p.id === this.planId))) {
          e.preventDefault(); e.stopImmediatePropagation();
          this.message(this.conflict ? 'More than one purchase options selector is active. Use only one subscription selector.' : 'Please wait for purchase options to update, then select a valid plan.');
        }
      }, {capture:true, signal:this.abort.signal});
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
    message(text) { this.status.textContent = text; this.status.hidden = !text; }
    state() { return {mode:this.mode, planId:this.planId}; }
    setPending() { this.pending = true; this.input.disabled = true; }
    restore(state) {
      this.pending = false;
      if (state) {
        const valid = this.plans.some(p => p.id === state.planId);
        this.mode = this.dataset.required === 'true' ? 'subscription' : (valid ? state.mode : 'one_time');
        this.planId = valid ? state.planId : this.plans[0]?.id || '';
        if (!valid && state.mode === 'subscription') this.message('Purchase options changed for this variant. Please review your selection.');
      }
      this.sync();
    }
    sync() {
      if (this.dataset.required === 'true') this.mode = 'subscription';
      const plan = this.plans.find(p => p.id === this.planId);
      const other = this.form && [...this.form.elements].some(el => el !== this.input && el.name === 'selling_plan' && !el.disabled && (!['radio','checkbox'].includes(el.type) || el.checked));
      this.conflict = Boolean(other);
      const active = this.mode === 'subscription' && plan && !this.pending && !other;
      this.input.value = active ? plan.id : '';
      this.input.disabled = !active;
      if (other) this.message('More than one purchase options selector is active. Use only one subscription selector.');
      if (!this.form) this.message('Purchase options require an Add to cart button in this product section.');
      this.boxes.forEach(box => {
        const selected = box.dataset.type === this.mode;
        box.dataset.selected = String(selected);
        box.classList.toggle('subscription__box--selected', selected);
        box.setAttribute('aria-checked', String(selected));
      });
      this.querySelectorAll('select').forEach(el => { el.value = this.planId; });
      this.querySelectorAll('input[data-selling-plan-id]').forEach(el => { el.checked = el.value === this.planId; });
      const selectedControl = active
        ? [...this.querySelectorAll('[data-selling-plan-id]')].find(el => String(el.dataset.sellingPlanId || el.value) === String(this.planId))
          || [...this.querySelectorAll('select')].flatMap(select => [...select.selectedOptions]).find(option => String(option.value) === String(this.planId))
        : null;
      const price = active ? Number(selectedControl?.dataset.price || plan?.price_cents || 0) : Number(this.dataset.basePrice || 0);
      const comparePrice = active ? Number(selectedControl?.dataset.comparePrice || plan?.compare_price_cents || price) : Number(this.dataset.baseComparePrice || price);
      const amountSaved = Math.max(0, comparePrice - price);
      const amountSavedRounded = Math.round(amountSaved / 100) * 100;
      const percentageSaved = comparePrice > 0 ? Math.floor((amountSaved * 100) / comparePrice) : 0;
      const money = value => {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(value, this.dataset.moneyFormat);
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:window.Shopify?.currency?.active || 'USD'}).format(value / 100);
      };
      const escape = value => { const node = document.createElement('span'); node.textContent = String(value ?? ''); return node.innerHTML; };
      const values = {
        subscription_name: plan?.subscription_name || this.dataset.subscriptionName || '',
        price: money(price),
        compare_price: money(comparePrice),
        amount_saved: money(amountSaved),
        amount_saved_rounded: money(amountSavedRounded),
        percentage_saved: `${percentageSaved}%`,
      };
      this.querySelectorAll(`${active ? '.sub-price-update' : '.one-time-price-update'}[data-text]`).forEach(el => {
        el.innerHTML = el.dataset.text.replace(/\[(subscription_name|price|compare_price|amount_saved|amount_saved_rounded|percentage_saved)\]/g, (_, key) => escape(values[key]));
      });
      this.dispatchEvent(new CustomEvent('selling-plan:change', {bubbles:true,detail:{sellingPlanId:active ? plan.id : '',subscription:Boolean(active),price,comparePrice,amountSaved,amountSavedRounded,percentageSaved,displayedPrices:this.dataset.displayedPrices || 'unit',updatePrices:this.dataset.updatePrices === 'true'}}));
      this.dispatchEvent(new CustomEvent('subscription-price:change', {bubbles:true,detail:{price,comparePrice,amountSaved,amountSavedRounded,percentageSaved,subscription:Boolean(active),sellingPlanId:active ? plan.id : '',displayedPrices:this.dataset.displayedPrices || 'unit'}}));
    }
  }
  customElements.define('dawn-purchase-options', PurchaseOptions);
})();
